import { FC } from 'react';
import { twMerge } from 'tailwind-merge';

import { ArrowIcon } from '../../../assets/icons/index';
import { AppFormattedMessage } from '../../../components/app-formatted-message/index';
import Button from '../../../components/button/index';
import { CrossfadeText } from '../../../components/motion/index';
import { SelectedCompanyCurrency } from '../../../components/selected-company-currency/index';
import { useNavigate } from '../../../hooks/index';
import { PageRoute } from '../../../types/page.types';
import { Snapshot } from '../../../types/snapshot.types';
import { toRound } from '../../../utils/get-rounded-number.util';
import { toNumber } from '../../../utils/to-number.util';
import { latestSnapshotId } from '../variables';

export type MobileDetailsProps = {
  className?: string;
  snapshot?: Snapshot;
  snapshotPanelKey?: string;
  valuationId?: string;
};

export const MobileDetails: FC<MobileDetailsProps> = ({
  snapshot,
  valuationId = latestSnapshotId,
  className,
  snapshotPanelKey = 'latest',
}) => {
  const navigate = useNavigate(PageRoute.CAP_TABLE);

  const issuedTotal = snapshot?.stats?.stakeholdersTypes?.reduce(
    (acc, curr) => (acc += curr.issuedPercentage || 0),
    0,
  );
  const dilutedTotal = snapshot?.stats?.stakeholdersTypes?.reduce(
    (acc, curr) => (acc += curr.dilutedPercentage || 0),
    0,
  );
  const votingTotal = snapshot?.stats?.stakeholdersTypes?.reduce(
    (acc, curr) => (acc += curr.votingPercentage || 0),
    0,
  );
  const noIssuedTotal = snapshot?.stats?.stakeholdersTypes?.reduce(
    (acc, curr) => (acc += curr.issued || 0),
    0,
  );
  const dilutedSharesTotal = snapshot?.stats?.stakeholdersTypes?.reduce(
    (acc, curr) => (acc += curr.diluted || 0),
    0,
  );
  const investmentTotal = 0;

  const metricsKey = `${snapshotPanelKey}-${issuedTotal}-${dilutedTotal}-${votingTotal}`;

  return (
    <div
      className={twMerge('flex w-full flex-col gap-4 rounded-md px-3 py-4 shadow-sm', className)}
    >
      <span className="text-base font-semibold text-gray-700">
        <AppFormattedMessage id="DETAILS" />
      </span>
      <div className="flex flex-col overflow-hidden rounded-md shadow-sm">
        <div className="grid grid-flow-row grid-cols-2 text-sm font-[450] text-gray-700">
          <div className="centered flex w-full flex-col gap-3 border border-gray-100 py-3">
            <span className="text-label-md font-medium">
              <AppFormattedMessage id="ISSUED_PERCENTAGE" />
            </span>
            <CrossfadeText textKey={`${metricsKey}-issued-pct`}>
              {toRound(toNumber(issuedTotal) * 100)}
            </CrossfadeText>
          </div>
          <div className="centered flex w-full flex-col gap-3 border border-gray-100 py-3">
            <span className="text-label-md font-medium">
              <AppFormattedMessage id="DILUTED_PERCENTAGE" />
            </span>
            <CrossfadeText textKey={`${metricsKey}-diluted-pct`}>
              {toRound(toNumber(dilutedTotal) * 100)}
            </CrossfadeText>
          </div>
          <div className="centered flex w-full flex-col gap-3 border border-gray-100 py-3">
            <span className="text-label-md font-medium">
              <AppFormattedMessage id="VOTING_PERCENTAGE" />
            </span>
            <CrossfadeText textKey={`${metricsKey}-voting-pct`}>
              {toRound(toNumber(votingTotal) * 100)}
            </CrossfadeText>
          </div>
          <div className="centered flex w-full flex-col gap-3 border border-gray-100 py-3">
            <span className="text-label-md font-medium">
              <AppFormattedMessage id="NO_ISSUED" />
            </span>
            <CrossfadeText textKey={`${metricsKey}-no-issued`}>
              {(noIssuedTotal || 0).toLocaleString('en-US')}
            </CrossfadeText>
          </div>
          <div className="centered flex w-full flex-col gap-3 border border-b-0 border-gray-100 py-3">
            <span className="text-label-md font-medium">
              <AppFormattedMessage id="DILUTED_SHARES" />
            </span>
            <CrossfadeText textKey={`${metricsKey}-diluted-shares`}>
              {(dilutedSharesTotal || 0).toLocaleString('en-US')}
            </CrossfadeText>
          </div>
          <div className="centered flex w-full flex-col gap-3 border border-b-0 border-gray-100 py-3">
            <span className="text-label-md font-medium">
              <AppFormattedMessage id="INVESTMENT" /> (<SelectedCompanyCurrency />)
            </span>
            <CrossfadeText textKey={`${metricsKey}-investment`}>
              {toRound(investmentTotal)}
            </CrossfadeText>
          </div>
        </div>
        {snapshot && (
          <Button
            className="flex w-full items-center justify-between rounded-none bg-brand-25 px-4 py-3"
            onClick={() => navigate([valuationId])}
            styleType="NONE"
          >
            <span className="text-sm font-[450] text-brand-700">
              <AppFormattedMessage id="VIEW_POSITIONS" />
            </span>
            <ArrowIcon className="rotate-180" iconColor="#2565C8" />
          </Button>
        )}
      </div>
    </div>
  );
};
